import { BiNotepad } from "react-icons/bi";
import { TiDocumentAdd } from "react-icons/ti";
import {
    Breadcrumb,
    Button,
    Input,
    message,
    Pagination,
    Select
} from "antd";
import { Link } from "react-router-dom";
import { DashboardOutlined, PlusSquareOutlined } from "@ant-design/icons";
import Header from "../../components/dashboard/header";
import { useState, useRef, useContext, useEffect } from "react";
import {
    useCreateMemo,
    useGetMemos,
    useGetPaginatedMemos,
    useUpdateMemo
} from "../../hooks/memo/memoQuery";
import { userStore } from "../../store/userStore";
import { useFetchAllFinancialYears } from "../../hooks/financialYear/financialYearQuery";
import { useGetMemoScope } from "../../hooks/memo-scopes/memoScopeQuery";
import RefreshContext from "../../context/refreshContext";
import { extractValueFromInputRef, scrollToTop } from "../../helpers/dom";
import { usePVTypes } from "../../hooks/voucherTypes/voucherTypeQuery";
import { useGetAllUnits } from "../../hooks/unit/unitQuery";
import { CREATE_MEMO } from "../../helpers/roles";
import {
    MemoTable,
    MemoModal
} from "../../components/memo";

const { Option } = Select;
const eventBus = document;

export default function Memo() {
    const [content, setContent] = useState("");
    const [file, setFile] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [fyear, setFyear] = useState("");
    const [filterYear, setFilterYear] = useState(null);
    const [boundry, setBoundry] = useState("");
    const [category, setCategory] = useState("");
    const [memo, setMemo] = useState({});
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [memoContentMode, setMemoContentMode] = useState(true);
    const [amount, setAmount] = useState(0);
    const [unit, setUnit] = useState(null);
    const [memoType, setMemoType] = useState("ADVANCE");

    // Pagination and filter states
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [filterCreatorId, setFilterCreatorId] = useState("");
    const [filterTitle, setFilterTitle] = useState("");
    const [filterType, setFilterType] = useState("");
    const [approvalStatus, setApprovalStatus] = useState("pending");

    const { flag, setFlag } = useContext(RefreshContext);
    const titleRef = useRef(null);
    const memoReference = useRef(null);
    const staffId = useRef(null);
    const user = userStore(state => state.user);

    const { years } = useFetchAllFinancialYears();
    const { units } = useGetAllUnits();
    const { structures } = usePVTypes();

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);
    
    const handleCreate = () => {
        setMemo({});
        setContent("");
        setFyear("");
        setCategory("");
        setAmount(0);
        setUnit(null);
        setMemoType("ADVANCE");
        setFile(null);
        setMemoContentMode(true);
        setIsOpen(true);
    };

    const currentUser = userStore(state => state.user);

    useEffect(() => {
        if(!user?.roles?.includes(CREATE_MEMO))
            staffId.current = user.staffId
    }, [user])

    useEffect(() => {
        // register our events we want to listen to
        eventBus.addEventListener("custom:edit", e => {
            setMemo(e.detail.memo);
            e.detail.memo?.financialYear && setFyear(e.detail.memo?.financialYear?.id);
            e.detail.memo?.category && setCategory(e.detail.memo?.category?.id);
            e.detail.memo?.unit && setUnit(e.detail.memo?.unit?.id ?? null);
            e.detail.memo?.amount && setAmount(e.detail.memo?.amount ?? 0);
            e.detail.memo?.memoType && setMemoType(e.detail.memo?.memoType);
            e.detail.memo && setMemoContentMode(!(e.detail.memo?.documentPath ?? false));
            let parsedContent = "";
            try {
                // Proper handling of the content parsing
                if (e.detail.memo?.content) {
                    const contentObj = e.detail.memo.content;
                    parsedContent = contentObj || "";
                    console.log(parsedContent);
                }
            } catch (error) {
                console.error("Error parsing memo content:", error);
                parsedContent = "";
            }
            setContent(parsedContent);
            handleOpen();
        });

        // on update
        eventBus.addEventListener("custom:update", e => {
            handleYearSearch(null);
        });

        scrollToTop();
    }, []);

    const memoScopes = useGetMemoScope();
    const { years: financialYears } = useFetchAllFinancialYears();
    const createMemo = useCreateMemo();
    const updateMemo = useUpdateMemo();
    const getMemos = useGetMemos();
    const getPaginatedMemos = useGetPaginatedMemos();

    const handleSubmit = async () => {
        const f = new FormData();
        f.append("title", extractValueFromInputRef(titleRef));
        // Store the HTML content directly
        f.append("content", content);

        if (file) {
            f.append("file", file);
        } 

        f.append("financialYear", fyear);
        f.append('category', category);
        f.append("staffId", extractValueFromInputRef(staffId));
        f.append("memoType", memoType);

        if (!memoContentMode)
            f.append("memoReference", extractValueFromInputRef(memoReference));

        f.append("amount", amount);
        f.append("unitId", unit);

        const { message: response } = await createMemo(f);
        message.success(response ?? "memo created successfully");
        setIsOpen(false);
        setFlag(!flag);
    }

    const handleUpdate = async () => {
        const f = new FormData();
        f.append("title", extractValueFromInputRef(titleRef));

        // Store the HTML content directly
        f.append("content", content);

        if (file) {
            f.append("file", file);
        } 

        f.append("financialYear", fyear);
        f.append('category', category);
        f.append("staffId", extractValueFromInputRef(staffId));
        f.append("memoType", memoType);


        f.append("amount", amount);
        f.append("unitId", unit);

        const { message: response } = await updateMemo(memo.id, f);
        message.success(response ?? "memo update successfully");
        setIsOpen(false);
        setFlag(!flag);
        handleYearSearch(null);
    }

    const handleYearSearch = async (value) => {
        if (!!value) {
            setFilterYear(value);
        }
        const req = value ?? filterYear;
        if (!!req) {
            try {
                setLoading(true);
                const response = await getPaginatedMemos(
                    page,
                    size,
                    req,
                    filterCreatorId || undefined,
                    filterTitle || undefined,
                    filterType || undefined,
                    approvalStatus || undefined
                );
                setLoading(false);
                setData(response?.content ?? []);
                setTotalElements(response?.totalElements ?? 0);
            } catch (error){
                setData([])
                setTotalElements(0)
            }finally {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        handleYearSearch()
    }, [page])

    // Handle pagination change
    const handlePageChange = (newPage, newSize) => {
        const nPage = newPage - 1;
        setPage(nPage); // API uses 0-based indexing
        setSize(newSize);
    }

    return (
        <>
            <div className="main-container">
                <Header />
                <div className="main-body">
                    <div className="info-bar">
                        <Breadcrumb>
                            <Breadcrumb.Item>
                                <DashboardOutlined className="dashboard-icon" />
                                <Link to={"/dashboard"}>Dashboard</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <BiNotepad />
                                <Link to={"/dashboard"}>Memo</Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <TiDocumentAdd />
                                <Link to="/dashboard/memo/create">Manage</Link>
                            </Breadcrumb.Item>
                        </Breadcrumb>
                        <div style={{ display: 'flex', gap: '1em' }}>
                            <Button style={{ backgroundColor: "green", color: "#FFFFFF" }} onClick={handleCreate}>
                                <PlusSquareOutlined />
                                Create Memo
                            </Button>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                        <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Filters</div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <Select
                                showSearch
                                placeholder="Filter By Year"
                                onChange={handleYearSearch}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                style={{
                                    width: 150,
                                }}
                                options={[...years.map(year => ({ value: year.id, label: year.value }))]}
                            />
                            <Input
                                placeholder="Filter by Title"
                                style={{ width: 150 }}
                                value={filterTitle}
                                onChange={(e) => setFilterTitle(e.target.value)}
                                // onPressEnter={() => handleYearSearch(filterYear)}
                            />
                            <Input
                                placeholder="Filter by Creator ID"
                                style={{ width: 150 }}
                                value={filterCreatorId}
                                onChange={(e) => setFilterCreatorId(e.target.value)}
                                // onPressEnter={() => handleYearSearch(filterYear)}
                            />
                            <Select
                                placeholder="Filter by Type"
                                style={{ width: 150 }}
                                value={filterType || undefined}
                                onChange={(value) => {
                                    setFilterType(value);
                                    // handleYearSearch(filterYear)
                                }}
                                allowClear
                                options={[
                                    { value: 'CLAIM', label: 'CLAIM' },
                                    { value: 'ADVANCE', label: 'ADVANCE' }
                                ]}
                            />
                            <Select
                                placeholder="Filter by Approval Status"
                                style={{ width: 180 }}
                                value={approvalStatus}
                                onChange={(value) => {
                                    setApprovalStatus(value);
                                    // handleYearSearch(filterYear)
                                }}
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'approved', label: 'Approved' }
                                ]}
                            />
                            <Button
                                type="primary"
                                onClick={() => handleYearSearch(filterYear)}
                                style={{ marginLeft: '5px' }}
                            >
                                Search
                            </Button>
                        </div>
                    </div>

                    <MemoTable
                        data={data}
                        loading={loading}
                        totalElements={totalElements}
                        page={page}
                        pageSize={size}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            <MemoModal
                isOpen={isOpen}
                handleClose={handleClose}
                memo={memo}
                content={content}
                setContent={setContent}
                memoContentMode={memoContentMode}
                setMemoContentMode={setMemoContentMode}
                financialYears={financialYears}
                structures={structures}
                units={units}
                fyear={fyear}
                setFyear={setFyear}
                category={category}
                setCategory={setCategory}
                amount={amount}
                setAmount={setAmount}
                unit={unit}
                setUnit={setUnit}
                file={file}
                setFile={setFile}
                memoType={memoType}
                setMemoType={setMemoType}
                handleSubmit={handleSubmit}
                handleUpdate={handleUpdate}
                titleRef={titleRef}
                memoReference={memoReference}
                staffId={staffId}
            />
        </>
    );
}
